import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';

export const settingsKeys = {
  all: ['settings'] as const,
};

/** Mirrors public.settings. Every value here is business policy, never a constant. */
export interface Settings {
  id: number;
  sla_first_contact_minutes: number;
  sla_escalation_minutes: number[];
  health_at_risk_days: number;
  health_dormant_days: number;
  health_dead_days: number;
  stage_close_probability: Record<string, number>;
  assignment_strategy: string;
  commission_company_percent: string;
  commission_agent_percent: string;
  commission_team_leader_percent: string;
  working_days: number[];
  working_hours_start: string;
  working_hours_end: string;
  business_timezone: string;
  retention_audit_months: number;
  retention_activity_months: number;
  retention_notification_days: number;
  leakage_uncontacted_hours: number;
  leakage_stalled_days: number;
}

// A single string literal, not select('*'): Supabase infers the row shape from the
// literal, and '*' leaves every column as `any`. See features/admin/useUsers.ts.
// prettier-ignore
const SETTINGS_COLUMNS = 'id, sla_first_contact_minutes, sla_escalation_minutes, health_at_risk_days, health_dormant_days, health_dead_days, stage_close_probability, assignment_strategy, commission_company_percent, commission_agent_percent, commission_team_leader_percent, working_days, working_hours_start, working_hours_end, business_timezone, retention_audit_months, retention_activity_months, retention_notification_days, leakage_uncontacted_hours, leakage_stalled_days';

export function useSettings() {
  return useQuery({
    queryKey: settingsKeys.all,
    queryFn: async (): Promise<Settings> => {
      const { data, error } = await supabase
        .from('settings')
        .select(SETTINGS_COLUMNS)
        .eq('id', 1)
        .single();

      if (error) throw error;
      return data;
    },
  });
}

/**
 * settings is read-only from the API — INSERT and UPDATE are revoked from every
 * client role — so saving goes through admin_update_settings(), which re-checks the
 * caller against profiles, rejects unknown keys outright, and is validated by the
 * settings_validate trigger before anything is written.
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: Partial<Settings>) =>
      rpc<Settings>('admin_update_settings', {
        p_patch: patch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
}
